import {
  Flex,
  IconMultipleImage,
  RemoteImage,
  Typography,
} from '@banhala/ui-kit';
import { useEffect, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import Svg, { Defs, LinearGradient, Rect, Stop } from 'react-native-svg';
import type { MumulLivePost } from 'shared/src/api';
import { isNotNil, isWeb } from 'shared/src/utils';
import { formatPostedAt } from '../utils/formatPostedAt';
import { IconPlayBadge } from './IconPlayBadge';

// react-native-video v7 네이티브 전용
const RNVideo = !isWeb ? require('react-native-video') : null;

const WHITE_90 = 'rgba(255,255,255,0.9)';

/** 카드에 그릴 미디어는 항상 첫 번째 아이템. 영상이 아니면 null */
export const getFirstVideo = (post: MumulLivePost) => {
  const [firstMedia] = post.media_list;

  return firstMedia?.type === 'VIDEO' ? firstMedia : null;
};

type Props = {
  post: MumulLivePost;
  width: number;
  height: number;
  /** 자동 재생 대상 카드 여부. 한 화면에서 1개만 true. */
  isPlaying: boolean;
  /** 마켓홈이 화면에 보이는 중인지. false 면 재생을 멈춘다. */
  isForeground: boolean;
  onPress: () => void;
};

export const MumulLiveContentCard = ({
  post,
  width,
  height,
  isPlaying,
  isForeground,
  onPress,
}: Props) => {
  const video = getFirstVideo(post);
  const showVideo = isPlaying && isNotNil(video) && isNotNil(RNVideo);

  return (
    <Flex
      width={width}
      height={height}
      borderRadius={6}
      overflow={'hidden'}
      onPress={onPress}
      testID={`무물콘텐츠카드_${post.sno}`}
    >
      <RemoteImage uri={post.thumbnail_url} width={width} height={height} />

      {showVideo && (
        <AutoPlayVideo
          videoUrl={video.url}
          cardWidth={width}
          cardHeight={height}
          videoWidth={post.width}
          videoHeight={post.height}
          isForeground={isForeground}
        />
      )}

      <View style={StyleSheet.absoluteFill} pointerEvents={'none'}>
        <Svg width={'100%'} height={'100%'}>
          <Defs>
            <LinearGradient
              id={'mumulCardScrim'}
              x1={'0'}
              y1={'1'}
              x2={'0'}
              y2={'0'}
            >
              <Stop offset={'0.227%'} stopColor={'#000000'} stopOpacity={0.7} />
              <Stop offset={'54.059%'} stopColor={'#000000'} stopOpacity={0} />
            </LinearGradient>
          </Defs>
          <Rect width={'100%'} height={'100%'} fill={'url(#mumulCardScrim)'} />
        </Svg>
      </View>

      <Flex position={'absolute'} top={8} right={8}>
        {post.post_type === 'VIDEO' && <IconPlayBadge />}
        {post.post_type === 'CAROUSEL' && (
          <IconMultipleImage width={24} height={24} fill={'white'} />
        )}
      </Flex>

      <Flex position={'absolute'} left={12} right={12} bottom={10} gap={4}>
        <Typography type={'subtitle5'} rawColor={WHITE_90}>
          {formatPostedAt(post.posted_at)}
        </Typography>
        <Typography
          type={'body3-relaxed'}
          color={'content_always_white'}
          numberOfLines={2}
        >
          {post.caption}
        </Typography>
      </Flex>
    </Flex>
  );
};

/**
 * 카드를 꽉 채우는(cover) 영상 박스 크기. TextureView 에서는 resizeMode="cover" 가
 * 동작하지 않아 필러/레터박스가 생기므로, 원본 비율로 확대한 크기를 직접 계산해
 * 카드 밖으로 넘긴 뒤 부모의 overflow:hidden 으로 자른다.
 */
const getCoverSize = (
  cardWidth: number,
  cardHeight: number,
  videoWidth: number,
  videoHeight: number,
) => {
  if (!(videoWidth > 0) || !(videoHeight > 0)) {
    return { width: cardWidth, height: cardHeight, left: 0, top: 0 };
  }
  const scale = Math.max(cardWidth / videoWidth, cardHeight / videoHeight);
  const width = videoWidth * scale;
  const height = videoHeight * scale;

  return {
    width,
    height,
    left: (cardWidth - width) / 2,
    top: (cardHeight - height) / 2,
  };
};

type AutoPlayVideoProps = {
  videoUrl: string;
  cardWidth: number;
  cardHeight: number;
  videoWidth: number;
  videoHeight: number;
  isForeground: boolean;
};

/** react-native-video v7 — useVideoPlayer + VideoView 조합. 음소거 반복 재생. */
const AutoPlayVideo = ({
  videoUrl,
  cardWidth,
  cardHeight,
  videoWidth,
  videoHeight,
  isForeground,
}: AutoPlayVideoProps) => {
  const Video = RNVideo;

  const player = Video.useVideoPlayer(
    { uri: videoUrl },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (p: Record<string, any>) => {
      p.muted = true;
      p.loop = true;
      p.play();
    },
  );

  // 딥링크로 네이티브 화면이 위에 덮여도 RN 화면은 unmount 되지 않으므로
  // 포그라운드 여부로 재생을 직접 제어한다.
  useEffect(() => {
    if (isForeground) {
      player.play();
    } else {
      player.pause();
    }
  }, [isForeground, player]);

  // 첫 프레임 준비 전에는 빈 서피스(검은 화면)가 썸네일을 덮으므로 투명하게 둔다.
  const [isReady, setIsReady] = useState(false);
  useEffect(() => {
    const readySub = player.addEventListener('onReadyToDisplay', () =>
      setIsReady(true),
    );
    const playbackSub = player.addEventListener(
      'onPlaybackStateChange',
      (data: { isPlaying: boolean }) => {
        if (data.isPlaying) setIsReady(true);
      },
    );

    return () => {
      readySub.remove();
      playbackSub.remove();
    };
  }, [player]);

  const VideoViewComponent = Video.VideoView;
  const cover = getCoverSize(cardWidth, cardHeight, videoWidth, videoHeight);

  return (
    <View
      style={{
        position: 'absolute',
        left: cover.left,
        top: cover.top,
        width: cover.width,
        height: cover.height,
      }}
      pointerEvents={'none'}
    >
      <VideoViewComponent
        player={player}
        style={{
          width: cover.width,
          height: cover.height,
          opacity: isReady ? 1 : 0,
        }}
        resizeMode={'cover'}
        surfaceType={'texture'}
      />
    </View>
  );
};
